import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import InnerTitle2 from "../../components/InnerTitle2";
import { Inquiry, subjectList } from "../../services/cms.service";
import Swal from "sweetalert2";
import { MESSAGE_MAX_LENGTH, NAME_MAX_LENGTH, SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUser } from "../../../../utils/commonfunction";

function ContactUs() {
  const { t } = useTranslation();
  const [globalData, setGlobalData] = useState({});
  const [subjects, setSubjects] = useState([]);
  const myGlobalData = useSelector((state) => state.globalData);

  useEffect(() => {
    if (myGlobalData && myGlobalData?.data) {
      const settingsData = JSON.parse(myGlobalData?.data);
      setGlobalData(settingsData);
    }
  }, [myGlobalData]);

  useEffect(() => {
    subjectList()
      .then((res) => {
        if (res && res.data) {
          setSubjects(res.data);
        }
      })
      .catch((err) => {
        console.log("Error fetching subjects:", err);
      });
  }, []);

  const userData = getUser(ROLE.USER) || {};

  const formik = useFormik({
    initialValues: {
      name: userData?.name || "",
      email: userData?.email || "",
      subject: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .max(NAME_MAX_LENGTH, t("Name_Validation_maximum_length")),
      email: Yup.string().email("Invalid email format").required("Email is required"),
      subject: Yup.string().required("Subject is required"),
      message: Yup.string()
        .required("Message is required")
        .max(MESSAGE_MAX_LENGTH, t("Message_Validation_maximum_length")),
    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await Inquiry(values);

        if (response.success) {
          Swal.fire({
            icon: "success",
            text: response.message || "Message sent successfully!",
            ...SWAL_SETTINGS,
          });
          resetForm();
        } else {
          Swal.fire({
            icon: "error",
            text: response.message || "Something went wrong!",
            ...SWAL_SETTINGS,
          });
        }
      } catch (error) {
        console.log(error);
        Swal.fire({
          icon: "error",
          text: "Something went wrong!",
          ...SWAL_SETTINGS,
        });
      }
    },
  });

  return (
    <>
      <div className="contact-bg">
        <InnerTitle2 title={t("header.contact", "Contact Us")} />

        <div className="contact-content-bg py-10">
          <div className="container">
            <div className="w-full mx-auto bg-primary-400 rounded-[22px] flex flex-col lg:flex-row">
              {/* === LEFT PANEL === */}
              <div className="lg:w-5/12 p-4 sm:p-14 text-white flex flex-col">
                <h2 className="headings-h1 !leading-relaxed mb-4">
                  {t("contact.reach_out", "Reach Out")} <br />
                  {t("contact.guide_you", "We're Here to Guide You")}
                </h2>

                <div className="mt-0 flex gap-[18px]">
                  <span>
                    Have a question or need assistance? We're here to help. Contact us anytime, and our team will get back to you as soon as possible.
                  </span>
                </div>
              </div>

              {/* === RIGHT PANEL (FORM) === */}
              <div className="lg:w-7/12 p-4 sm:p-14 bg-white shadow-[0px_2px_24px_rgba(0,0,0,0.05)] rounded-tr-[20px] rounded-br-[20px]">
                <h2 className="headings-h1 text-[var(--black-scale-900)] mb-8 !leading-relaxed">
                  {t("contact.need_help", "Need Help? Our Tech Navigates for You")}
                </h2>

                {/* FORM START */}
                <form onSubmit={formik.handleSubmit}>
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Name */}
                    <div className="flex-1 form-group">
                      <label>{t("name", "Name")}</label>
                      <input
                        type="text"
                        name="name"
                        placeholder={t("placeholder.name", "Enter name")}
                        className="form-control-custom"
                        {...formik.getFieldProps("name")}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <div className="text-red-500 text-sm">{formik.errors.name}</div>
                      )}
                    </div>

                    {/* Email */}
                    <div className="flex-1 form-group">
                      <label>{t("email", "Email ID")}</label>
                      <input
                        type="email"
                        name="email"
                        placeholder={t("placeholder.email", "Enter email ID")}
                        className="form-control-custom"
                        {...formik.getFieldProps("email")}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="text-red-500 text-sm">{formik.errors.email}</div>
                      )}
                    </div>
                  </div>

                  {/* Subject Dropdown */}
                  <div className="form-group">
                    <label>{t("label_subject", "Subject")}</label>
                    <select
                      name="subject"
                      className="form-control-custom"
                      {...formik.getFieldProps("subject")}
                    >
                      <option value="">{t("select_subject", "Select Subject")}</option>
                      {subjects.map((sub) => (
                        <option key={sub._id} value={sub.name}>
                          {sub.name}
                        </option>
                      ))}
                    </select>
                    {formik.touched.subject && formik.errors.subject && (
                      <div className="text-red-500 text-sm">{formik.errors.subject}</div>
                    )}
                  </div>

                  {/* Message */}
                  <div className="form-group">
                    <label>{t("label_message", "Message")}</label>
                    <textarea
                      name="message"
                      rows="4"
                      placeholder={t("placeholder.message", "Enter")}
                      className="form-control-custom"
                      {...formik.getFieldProps("message")}
                    ></textarea>
                    {formik.touched.message && formik.errors.message && (
                      <div className="text-red-500 text-sm">{formik.errors.message}</div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button type="submit" className="btn btn-pri w-full">
                    {t("web_inquiry_form_btn_submit", "Send")}
                  </button>
                </form>
                {/* FORM END */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ContactUs;
